import some from 'lodash-es/some'
import * as yup from 'yup'

import {
  MIN_POLL_OPTIONS,
  STREAMABLE_REGEX,
  VIMEO_LIVESTREAM_REGEX,
  VIMEO_REGEX,
  YOUTUBE_REGEX,
} from '../../consts'
import type { PostAccess, PostType, VideoPostType } from '../../types'
import { FormFields } from './types'

type Params = {
  pollLenghValidation?: boolean
  publicVideo?: boolean
  scheduleStatus?: string
}

export const getValidationSchema = (validation?: Params) => {
  const pollLength = (shouldValidate?: boolean) => {
    if (!shouldValidate) return

    return {
      [FormFields.pollLength]: yup.object().when([FormFields.postType], (postType: PostType) => {
        if (postType === 'Poll') {
          return yup
            .object()
            .required()
            .test('pollLength', 'Poll must be at least one minute long.', (value) => {
              return value && some(value, (property) => property !== '0' && property !== undefined)
            })
        }

        return yup.mixed()
      }),
    }
  }

  return yup.object().shape({
    [FormFields.liveAt]: yup.date().when([FormFields.mode], (mode: string) => {
      if (mode === 'update' && validation?.scheduleStatus === 'Done') {
        return yup.date().max(new Date(), 'Please select date from the past')
      }

      return yup.date()
    }),
    [FormFields.access]: yup.string().required(),
    [FormFields.postType]: yup.string(),
    [FormFields.title]: yup.string().required().min(3),
    [FormFields.description]: yup
      .string()
      .test(
        'Must have less than 100000 characters',
        'Must have less than 100000 characters',
        (value) => {
          if (!value) return true

          const plainText = value.replace(/<[^>]*>/g, '')

          return plainText.length <= 100000
        },
      )
      // Validate if the description has any text content
      .test('This field is required.', 'This field is required.', (value) => {
        if (!value) return true

        const tmp = document.createElement('div')
        tmp.innerHTML = value || ''

        if (!tmp.textContent) return false

        if (!tmp.textContent.trim()) return false

        return true
      }),
    // [FormFields.images]: yup.array().when([FormFields.postType], (postType: PostType) => {
    //   if (postType === 'Image') {
    //     return yup.array().min(1, 'This field is required.').required()
    //   }

    //   return yup.array()
    // }),
    [FormFields.images]: yup.array().when([FormFields.postType], (postType: PostType) => {
      if (postType === 'Image') {
        return yup.array().min(1, 'This field is required.').required()
      }

      return yup.array()
    }),
    [FormFields.videoType]: yup.string().required(),
    [FormFields.video]: yup
      .mixed()
      .when([FormFields.postType, FormFields.videoType], (postType: PostType, videoType) => {
        if (postType === 'Video' && videoType === 'upload') {
          return yup
            .mixed()
            .required()
            .test('video', '', function (this: yup.TestContext) {
              const access = this.parent[FormFields.access] as PostAccess
              const postType = this.parent[FormFields.postType] as PostType
              const videoType = this.parent[FormFields.videoType] as VideoPostType

              if (validation?.publicVideo) return true
              if (postType !== 'Video') return true
              if (videoType !== 'upload') return true

              if (
                access === 'Public' ||
                access === 'FreeMembers' ||
                access === 'FreeAndPaidMembers'
              ) {
                return false
              }

              return true
            })
        }

        return yup.mixed()
      }),
    [FormFields.videoThumbnail]: yup.mixed(),
    [FormFields.videoUrl]: yup
      .string()
      .when([FormFields.postType, FormFields.videoType], (postType: PostType, videoType) => {
        if ((postType === 'Video' || postType === 'VideoEmbed') && videoType === 'embed') {
          return yup
            .string()
            .required()
            .test('videoUrl', 'This is not a valid YouTube, Vimeo or Streamable URL.', (value) => {
              return !!(
                value?.match(YOUTUBE_REGEX) ||
                value?.match(VIMEO_REGEX) ||
                value?.match(VIMEO_LIVESTREAM_REGEX) ||
                value?.match(STREAMABLE_REGEX)
              )
            })
        }

        return yup.string()
      }),
    [FormFields.videoSeries]: yup.array().of(
      yup.object().shape({
        id: yup.number(),
      }),
    ),
    [FormFields.audio]: yup.mixed().when([FormFields.postType], (postType: PostType) => {
      if (postType === 'Audio') {
        return yup.mixed().required('Audio file is required.')
      }

      return yup.mixed()
    }),
    [FormFields.audioThumbnail]: yup.mixed().when([FormFields.postType], (postType: PostType) => {
      if (postType === 'Audio') {
        return yup.mixed().required('Audio thumbnail is required.')
      }

      return yup.mixed()
    }),
    [FormFields.pollOptions]: yup.array().when([FormFields.postType], (postType: PostType) => {
      if (postType === 'Poll') {
        return yup
          .array()
          .min(MIN_POLL_OPTIONS)
          .required()
          .of(yup.string().required().min(1).max(80))
      }

      return yup.array()
    }),
    [FormFields.tierIds]: yup
      .object()
      .when([FormFields.access], (access: 'MembersOnly' | 'Plans') => {
        if (access === 'Plans') {
          return yup
            .object()
            .test(
              'isEmpty',
              'At least one selected tier is required.',
              (value) => Object.values(value).filter(Boolean).length > 0,
            )
        }

        return yup.object()
      }),
    ...pollLength(validation?.pollLenghValidation),
  })
}
